import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/App";

// R14.2-A — Install global error handlers BEFORE React mounts so they
// intercept any uncaught exception from race-y unmount cleanups (e.g.
// `Cannot close a closed AudioContext` from PreRoomPreview cleanup
// during the phase=preview → connecting transition). The handlers
// preventDefault + zero returnValue + stopImmediatePropagation early,
// then surface the error as a red toast via the `sr-toast` bridge so
// the user still sees what happened — but the CRA / webpack-dev-server
// dev overlay never takes over and locks them out of the room.
//
// stopImmediatePropagation is critical: webpack-dev-server's overlay
// listens for `window.error` in bubble phase. Our capture-phase listener
// fires first; without stopImmediate, the WDS listener still runs and
// shows its full-screen overlay even though we called preventDefault.
//
// As a belt-and-suspenders, we also CSS-hide and MutationObserver-remove
// the WDS overlay iframe (`#webpack-dev-server-client-overlay`) if it
// ever manages to appear, since the toast bridge fully replaces it.
//
// IMPORTANT: we explicitly do NOT swallow programmer-error throws that
// occur during normal render; we only call preventDefault on uncaught
// errors that have already reached `window` (i.e. nothing else can act
// on them). React's error boundaries continue to work normally.
(() => {
  if (typeof window === "undefined") return;
  const dispatchToast = (msg) => {
    try {
      window.dispatchEvent(new CustomEvent("sr-toast", {
        detail: { type: "error", msg },
      }));
    } catch { /* */ }
  };
  const errHandler = (e) => {
    // eslint-disable-next-line no-console
    console.error("[UNCAUGHT global]", e.message, e.error);
    try { e.preventDefault(); } catch { /* */ }
    try { e.returnValue = ""; } catch { /* */ }
    try { e.stopImmediatePropagation(); } catch { /* */ }
    dispatchToast(`Internal error — ${e.message || "see console"}`);
  };
  const rejHandler = (e) => {
    // eslint-disable-next-line no-console
    console.error("[UNCAUGHT promise global]", e.reason);
    try { e.preventDefault(); } catch { /* */ }
    try { e.returnValue = ""; } catch { /* */ }
    try { e.stopImmediatePropagation(); } catch { /* */ }
    const msg = e.reason?.message || String(e.reason || "see console");
    dispatchToast(`Internal error (promise) — ${msg}`);
  };
  window.addEventListener("error", errHandler, true);
  window.addEventListener("unhandledrejection", rejHandler, true);

  // Dev-only safety net: aggressively hide + remove the WDS overlay
  // iframe. The toast bridge surfaces all errors to the user already,
  // so the dev overlay is pure noise here.
  if (process.env.NODE_ENV !== "production") {
    const css = document.createElement("style");
    css.textContent =
      "#webpack-dev-server-client-overlay,#webpack-dev-server-client-overlay-div{display:none!important;visibility:hidden!important;}";
    (document.head || document.documentElement).appendChild(css);
    const nuker = () => {
      document.querySelectorAll(
        "#webpack-dev-server-client-overlay, #webpack-dev-server-client-overlay-div",
      ).forEach((el) => { try { el.remove(); } catch { /* */ } });
    };
    nuker();
    if (typeof MutationObserver !== "undefined") {
      new MutationObserver(nuker).observe(document.documentElement, {
        childList: true, subtree: true,
      });
    }
  }
})();

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
